import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheck, Lock, MessageSquare, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type {
  ConversationPriority,
  ConversationStatus,
  StaffConversation,
  SupportAttachment,
  SupportMessage,
} from '../../../common/models/support.model';
import { CONVERSATION_PRIORITIES } from '../../../common/models/support.model';
import { CONVERSATION_STATUS_META } from '../support-meta';
import { MessageTimeline } from '../components/MessageTimeline';
import { Composer } from '../components/Composer';
import { AssigneePicker } from './StaffPeople';

const PRIORITY_KEYS: Record<ConversationPriority, string> = {
  low: 'SUPPORT.PRIORITY.LOW',
  normal: 'SUPPORT.PRIORITY.NORMAL',
  high: 'SUPPORT.PRIORITY.HIGH',
  urgent: 'SUPPORT.PRIORITY.URGENT',
};

/** Middle pane: who and what, the conversation, and a reply box that can also leave internal notes. */
export function StaffThread({
  conversation,
  messages,
  me,
  upload,
  onReply,
  onUpdate,
}: {
  conversation?: StaffConversation;
  messages?: SupportMessage[];
  me?: string;
  upload: (file: File, onProgress: (f: number) => void) => Promise<SupportAttachment>;
  onReply: (payload: { body: string; attachments: SupportAttachment[]; internal: boolean }) => Promise<void>;
  onUpdate: (patch: Partial<Pick<StaffConversation, 'status' | 'priority' | 'assignee'>>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'reply' | 'note'>('reply');

  if (!conversation) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <MessageSquare className="size-5" />
        {t('SUPPORT.STAFF.PICK')}
      </div>
    );
  }

  const resolved = conversation.status === 'resolved';

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <header className="flex flex-col gap-2.5 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate font-medium" data-testid="thread-subject" title={conversation.subject}>{conversation.subject}</h2>
          <p className="truncate text-xs text-muted-foreground">
            {conversation.vendorName} · {conversation.createdBy?.name ? `${conversation.createdBy.name} (${conversation.createdBy.email})` : conversation.createdBy?.email}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={conversation.status} onValueChange={(v) => onUpdate({ status: v as ConversationStatus })}>
            <SelectTrigger size="sm" className="w-44" aria-label={t('SUPPORT.STAFF.STATUS')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(['open', 'waiting', 'resolved'] as ConversationStatus[]).map((s) => (
                <SelectItem key={s} value={s}>
                  <span className={cn('size-1.5 rounded-full', CONVERSATION_STATUS_META[s].dot)} />
                  {t(CONVERSATION_STATUS_META[s].labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={conversation.priority} onValueChange={(v) => onUpdate({ priority: v as ConversationPriority })}>
            <SelectTrigger size="sm" className="w-28" aria-label={t('SUPPORT.STAFF.PRIORITY')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONVERSATION_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(PRIORITY_KEYS[p])}</SelectItem>)}
            </SelectContent>
          </Select>
          <AssigneePicker size="sm" className="w-40" value={conversation.assignee} me={me} onChange={(assignee) => onUpdate({ assignee })} />
          <Button variant="outline" size="sm" className="ms-auto" onClick={() => onUpdate({ status: resolved ? 'open' : 'resolved' })}>
            {resolved ? <RotateCcw data-icon="inline-start" /> : <CircleCheck data-icon="inline-start" />}
            {resolved ? t('SUPPORT.STAFF.REOPEN') : t('SUPPORT.STAFF.RESOLVE')}
          </Button>
        </div>
      </header>

      {messages ? (
        <MessageTimeline messages={messages} viewer="staff" />
      ) : (
        <div className="flex flex-1 flex-col justify-end gap-3 p-4">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-14 w-3/4 self-end" />
        </div>
      )}

      <div className="border-t p-3">
        <Composer
          key={conversation._id}
          upload={upload}
          tone={mode === 'note' ? 'note' : 'default'}
          placeholder={mode === 'note' ? t('SUPPORT.STAFF.NOTE_PLACEHOLDER') : t('SUPPORT.STAFF.REPLY_PLACEHOLDER', { vendor: conversation.vendorName })}
          onSend={(p) => onReply({ ...p, internal: mode === 'note' })}
          extra={
            <div className="flex rounded-lg bg-muted p-0.5 text-xs" role="radiogroup" aria-label={t('SUPPORT.STAFF.MODE')}>
              {(['reply', 'note'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={mode === m}
                  onClick={() => setMode(m)}
                  className={cn('flex items-center gap-1 rounded-md px-2 py-1 transition-colors', mode === m ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground')}
                >
                  {m === 'note' && <Lock className="size-3" />}
                  {m === 'reply' ? t('SUPPORT.STAFF.MODE_REPLY') : t('SUPPORT.STAFF.MODE_NOTE')}
                </button>
              ))}
            </div>
          }
        />
      </div>
    </div>
  );
}
